import { useMemo, useState } from "react";
import { motion } from "framer-motion";

const BLUE = "#4C9EDA";
const INK = "#122534";

export default function RiskReader() {
  const [base, setBase] = useState(4);
  const [rel, setRel] = useState(30);
  const after = useMemo(() => Math.min(100, +(base * (1 + rel / 100)).toFixed(1)), [base, rel]);
  const extra = +(after - base).toFixed(1);

  const dots = Array.from({ length: 100 }, (_, i) => (i < Math.round(base) ? "base" : i < Math.round(after) ? "extra" : "none"));

  return (
    <section className="relative overflow-hidden" style={{ background: "linear-gradient(165deg, #0F2A3D, #0A1C2A 72%)" }} data-testid="risk-reader">
      <span className="absolute right-[6%] -top-28 w-[32vw] h-[32vw] rounded-full halo" style={{ background: "radial-gradient(circle, rgba(76,158,218,0.36), transparent 68%)", filter: "blur(44px)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-semibold" style={{ background: "rgba(76,158,218,0.16)", color: "#9FD0F0" }}>
            <span className="w-1.5 h-1.5 rounded-full pulse-soft" style={{ background: BLUE }} />the reader for every frightening headline
          </span>
          <h2 className="font-display text-[34px] sm:text-[46px] leading-[1.02] mt-4 text-white">“A thirty per cent increase” is almost never what you think</h2>
          <p className="text-[15px] mt-4 max-w-sm text-white/55">Relative risk is a ratio. Absolute risk is people. Move both and watch how small most headlines actually are.</p>

          <div className="mt-8 space-y-6">
            <div>
              <div className="flex items-baseline justify-between">
                <p className="text-[13.5px] text-white/70">Baseline risk, per 100 people</p>
                <p className="font-display text-xl tabular-nums" style={{ color: BLUE }}>{base}</p>
              </div>
              <input type="range" min={1} max={30} value={base} onChange={(e) => setBase(Number(e.target.value))} className="w-full mt-2" style={{ accentColor: BLUE }} aria-label="baseline risk" data-testid="risk-base" />
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <p className="text-[13.5px] text-white/70">The headline says risk rises by</p>
                <p className="font-display text-xl tabular-nums" style={{ color: "#E9A08C" }}>{rel}%</p>
              </div>
              <input type="range" min={5} max={200} step={5} value={rel} onChange={(e) => setRel(Number(e.target.value))} className="w-full mt-2" style={{ accentColor: "#E9A08C" }} aria-label="relative increase" data-testid="risk-rel" />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-[2rem] p-7 sm:p-9" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.11)" }}>
            <div className="grid grid-cols-10 gap-1.5 sm:gap-2">
              {dots.map((k, i) => (
                <motion.span key={i} className="aspect-square rounded-[5px]" initial={false}
                  animate={{ background: k === "base" ? BLUE : k === "extra" ? "#E9A08C" : "rgba(255,255,255,0.09)" }}
                  transition={{ duration: 0.4, delay: (i % 10) * 0.008 }} data-testid={i === 0 ? "risk-dot-first" : undefined} />
              ))}
            </div>
            <div className="flex flex-wrap gap-6 mt-7">
              <div>
                <p className="font-display text-[34px] leading-none tabular-nums" style={{ color: BLUE }}>{base}</p>
                <p className="text-[11.5px] mt-1.5 text-white/45">people in 100 before</p>
              </div>
              <div>
                <p className="font-display text-[34px] leading-none tabular-nums" style={{ color: "#E9A08C" }}>{after}</p>
                <p className="text-[11.5px] mt-1.5 text-white/45">people in 100 after</p>
              </div>
              <div>
                <p className="font-display text-[34px] leading-none tabular-nums text-white">+{extra}</p>
                <p className="text-[11.5px] mt-1.5 text-white/45">the whole difference</p>
              </div>
            </div>
            <p className="text-[14px] mt-7 text-white/60">
              A {rel}% rise on a baseline of {base} in 100 means about {extra} more people in every hundred. That is the number that belongs in the headline, and it is the number that is almost always left out.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
